import { Query } from "@apollo/client/react/components";
import { LoadingIndicator } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import React, { ReactElement } from "react";
import CommentsQuery from "~/lib/queries/CommentsQuery";
import { ICommentsQuery, ICommentsQueryVariables } from "~/lib/queries/__generated__/CommentsQuery.generated";
import { ICommentTargetType, IReport } from "~/types/types";
import Auth from "~/components/auth/Auth";
import Comments from "~/components/comment/Comments";
import CreateComment from "~/components/comment/CreateComment";
import { WriteComment } from "~/components/comment/WriteComment";

interface IReportDiscussionProps {
  report: Pick<IReport, "_id">;
}

export default function ReportDiscussion({ report }: IReportDiscussionProps): ReactElement | null {
  return (
    <Auth>
      {({ user }) => {
        if (!user) {
          return null;
        }

        return (
          <Query<ICommentsQuery, ICommentsQueryVariables>
            query={CommentsQuery}
            variables={{
              targetId: report._id,
              targetType: ICommentTargetType.Report,
              cursor: null,
            }}
          >
            {({ data, loading }) => {
              if (loading) {
                return (
                  <div
                    css={{
                      display: "flex",
                      justifyContent: "center",
                      marginTop: 30,
                      backgroundColor: palette.white,
                      padding: 20,
                    }}
                  >
                    <LoadingIndicator />
                  </div>
                );
              }

              if (!data) {
                return null;
              }

              if (!data.comments) {
                return null;
              }

              if (!data.comments.items) {
                return null;
              }

              return (
                <div
                  css={{
                    backgroundColor: palette.white,
                    borderRadius: 5,
                    border: `solid 1px ${palette.lightestGray}`,
                  }}
                >
                  <div
                    css={{
                      fontSize: 12,
                      textTransform: "uppercase",
                      color: palette.mediumGray,
                      letterSpacing: 0.5,
                      padding: 15,
                      fontWeight: 500,
                      borderBottom: `1px solid ${palette.lightestGray}`,
                    }}
                  >
                    Discussion
                  </div>

                  <div css={{ padding: 20 }}>
                    <CreateComment targetId={report._id} targetType={ICommentTargetType.Report}>
                      {(createComment) => (
                        <WriteComment
                          actionText="Comment"
                          placeholder="Write your comment about this report"
                          onSubmit={createComment}
                        />
                      )}
                    </CreateComment>
                  </div>

                  {data.comments.items.length > 0 && (
                    <div
                      css={{
                        borderTop: `1px solid ${palette.lightestGray}`,
                        padding: 20,
                      }}
                    >
                      <Comments
                        targetId={report._id}
                        targetType={ICommentTargetType.Report}
                        comments={data.comments.items as any}
                      />
                    </div>
                  )}
                </div>
              );
            }}
          </Query>
        );
      }}
    </Auth>
  );
}
